import { Checkbox, Radio, Select, Space } from 'antd';
import moment, { type Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Alert, Button, useMessage } from 'components/ui';
import { UTCDatePicker } from 'components/UTCDatePicker';
import {
  type AtlasGqlSkydioMediaQuery,
  type AtlasGqlSkydioMediaQueryVariables,
  AtlasGqlSkydioImportSelectionMode,
  AtlasGqlSkydioIngestionStatusFilter,
  SkydioMediaDocument,
  useSkydioCredentialStatusQuery,
  useSkydioFlightsLazyQuery,
  useStartSkydioImportMutation,
} from 'types/atlas-graphql';
import { useApolloContext } from 'utils/apollo';
import type { SkydioFlight, SkydioMediaFile } from 'utils/skydio';

import { formatCatalogFlightLabel, getMediaKindOptions, normalizeFlightId } from './helpers';
import { loadFlightPreviews } from './loadFlightPreviews';
import {
  getSkydioImportProgress,
  saveSkydioImportRecord,
  SkydioImportStatusPanel,
} from './SkydioImportStatus';
import { SkydioMediaTable } from './SkydioMediaTable';
import { useSkydioImportStatusPolling } from './useSkydioImportStatusPolling';

type SkydioImportProps = {
  inspectionIds: string[];
  onComplete?: () => void;
};

export const toUtcPickerValue = (value: string) => (value ? moment.utc(value) : undefined);

const PREVIEW_PAGE_SIZE = 100;

const ingestionStatusFilters: Record<string, AtlasGqlSkydioIngestionStatusFilter> = {
  all: AtlasGqlSkydioIngestionStatusFilter.All,
  ingested: AtlasGqlSkydioIngestionStatusFilter.Ingested,
  new: AtlasGqlSkydioIngestionStatusFilter.New,
};

const SkydioAlert = styled(Alert)`
  .ant-alert-message {
    color: ${({ theme }) => theme.headerColor};
  }

  .ant-alert-description {
    color: ${({ theme }) => theme.textColor};
  }

  .ant-alert-icon {
    color: ${({ theme }) => theme.headerColor};
  }
`;

const ImportScope = styled(Radio.Group)`
  .ant-radio-wrapper {
    color: ${({ theme }) => theme.textColor};
    white-space: normal;
  }

  .ant-radio-wrapper-disabled {
    color: ${({ theme }) => theme.disabledTextColor};
  }
`;

export function SkydioImport({ inspectionIds, onComplete }: SkydioImportProps) {
  const { t } = useTranslation();
  const message = useMessage();
  const credentialStatus = useSkydioCredentialStatusQuery({ fetchPolicy: 'network-only' });
  const [queryFlights] = useSkydioFlightsLazyQuery({ fetchPolicy: 'network-only' });
  const { atlasClient } = useApolloContext();
  const [runStartImport, startImportMutation] = useStartSkydioImportMutation();
  const [media, setMedia] = useState<SkydioMediaFile[]>([]);
  const [flights, setFlights] = useState<SkydioFlight[]>([]);
  const [isLoadingFlightCatalog, setIsLoadingFlightCatalog] = useState(false);
  const [totalMediaCount, setTotalMediaCount] = useState<number>();
  const [completedFlights, setCompletedFlights] = useState(0);
  const previewAbort = useRef<AbortController>();
  const discoveryAbort = useRef<AbortController>();
  const [isDiscoveringFlights, setIsDiscoveringFlights] = useState(false);
  useEffect(
    () => () => {
      previewAbort.current?.abort();
      discoveryAbort.current?.abort();
    },
    []
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excludedUuids, setExcludedUuids] = useState<string[]>([]);
  const [capturedSince, setCapturedSince] = useState('');
  const [capturedBefore, setCapturedBefore] = useState('');
  const [kinds, setKinds] = useState<string[]>(['vehicleImage', 'vehicleRadiometricImage']);
  const [ingestedFilter, setIngestedFilter] = useState<string>('all');
  const [selectedFlightIds, setSelectedFlightIds] = useState<string[]>([]);
  const [flightSearch, setFlightSearch] = useState('');
  const [importFlightIds, setImportFlightIds] = useState<string[]>([]);
  const [loadedFilters, setLoadedFilters] = useState<{
    capturedBefore?: string;
    capturedSince?: string;
    flightIds?: string[];
    kinds: string[];
    status: AtlasGqlSkydioIngestionStatusFilter;
  }>();
  const [releaseOnSuccess, setReleaseOnSuccess] = useState(true);
  const [thermalIronbowEnabled, setThermalIronbowEnabled] = useState(true);
  const [recordId, setRecordId] = useState<string>();
  const [isImportInProgress, setIsImportInProgress] = useState(false);
  const targetInspectionId = inspectionIds[0];
  const {
    error: importMonitorError,
    retry: retryImportMonitor,
    status: importStatus,
  } = useSkydioImportStatusPolling(recordId);
  const handledTerminalRecord = useRef<string>();
  const handledMonitorError = useRef<Error>();
  const configured = credentialStatus.data?.skydioCredentialStatus.configured;
  const controlsDisabled = isImportInProgress || isLoadingFlightCatalog || isDiscoveringFlights;
  const currentFilters = {
    capturedBefore: capturedBefore || undefined,
    capturedSince: capturedSince || undefined,
    flightIds: selectedFlightIds.length ? selectedFlightIds : undefined,
    kinds,
    status: ingestionStatusFilters[ingestedFilter],
  };
  const previewIsCurrent =
    !!loadedFilters && JSON.stringify(loadedFilters) === JSON.stringify(currentFilters);
  const importCount = selectAllMatching
    ? totalMediaCount === undefined
      ? undefined
      : Math.max(0, totalMediaCount - excludedUuids.length)
    : selectedRowKeys.length;
  const allLabel =
    totalMediaCount === undefined
      ? t('inspection.skydio.importAllUnknown', 'Import all matching media')
      : t('inspection.skydio.importAllCount', 'Import all {{count}} matching media', {
          count: totalMediaCount,
        });
  const submitLabel = selectAllMatching
    ? importCount === undefined
      ? allLabel
      : t('inspection.skydio.importAllCount', 'Import all {{count}} matching media', {
          count: importCount,
        })
    : t('inspection.skydio.importSelectedCount', 'Import {{count}} selected media', {
        count: selectedRowKeys.length,
      });

  useEffect(() => {
    if (!credentialStatus.error) {
      return;
    }
    message.error(
      t(
        'inspection.skydio.credentialStatusLoadError',
        'Unable to load Skydio status: {{message}}',
        {
          message: credentialStatus.error.message,
        }
      )
    );
  }, [credentialStatus.error, message, t]);

  useEffect(() => {
    if (
      !recordId ||
      importStatus?.recordId !== recordId ||
      handledTerminalRecord.current === recordId
    ) {
      return;
    }
    if (importStatus.status === 'COMPLETE') {
      handledTerminalRecord.current = recordId;
      setIsImportInProgress(false);
      message.success(t('inspection.skydio.importComplete', 'Skydio import complete'));
      onComplete?.();
    } else if (importStatus.status === 'FAILED') {
      handledTerminalRecord.current = recordId;
      setIsImportInProgress(false);
      message.error(t('inspection.skydio.importFailed', 'Skydio import failed'));
    }
  }, [importStatus, message, onComplete, recordId, t]);

  useEffect(() => {
    if (!importMonitorError || handledMonitorError.current === importMonitorError) {
      return;
    }
    handledMonitorError.current = importMonitorError;
    setIsImportInProgress(false);
    message.error(
      t('inspection.skydio.importMonitorError', 'Unable to monitor Skydio import: {{message}}', {
        message: importMonitorError.message,
      })
    );
  }, [importMonitorError, message, t]);

  const filteredMedia = useMemo(() => {
    if (ingestedFilter === 'all') {
      return media;
    }
    return media.filter(
      item =>
        item.ingestionStatusKnown !== false && item.ingested === (ingestedFilter === 'ingested')
    );
  }, [ingestedFilter, media]);

  const flightOptions = useMemo(() => {
    return [
      ...new Map(flights.map(flight => [normalizeFlightId(flight.flightId), flight])).values(),
    ].map(flight => ({
      label: formatCatalogFlightLabel(flight),
      value: normalizeFlightId(flight.flightId),
    }));
  }, [flights]);

  const selectedMedia = useMemo(
    () => media.filter(item => selectedRowKeys.includes(item.uuid)),
    [media, selectedRowKeys]
  );
  const progress = useMemo(
    () =>
      getSkydioImportProgress({
        recordId,
        selectedCount: selectedMedia.length,
        status: importStatus?.recordId === recordId ? importStatus : undefined,
        t,
      }),
    [importStatus, recordId, selectedMedia.length, t]
  );

  const loadMedia = async () => {
    if (controlsDisabled) {
      return;
    }
    const controller = new AbortController();
    previewAbort.current = controller;
    setIsLoadingFlightCatalog(true);
    setCompletedFlights(0);
    setLoadedFilters(undefined);
    try {
      const loadMediaPage = async (selectedFlightId?: string) => {
        const response = await atlasClient.query<
          AtlasGqlSkydioMediaQuery,
          AtlasGqlSkydioMediaQueryVariables
        >({
          query: SkydioMediaDocument,
          fetchPolicy: 'network-only',
          context: { fetchOptions: { signal: controller.signal } },
          variables: {
            input: {
              filters: {
                capturedBefore: capturedBefore || undefined,
                capturedSince: capturedSince || undefined,
                flightId: selectedFlightId,
                inspectionIds: targetInspectionId ? [targetInspectionId] : undefined,
                kinds,
                pageNumber: 1,
                perPage: PREVIEW_PAGE_SIZE,
              },
            },
          },
        });
        if (response.error) {
          throw response.error;
        }
        const page = response.data?.skydioMedia;
        if (!page) {
          throw new Error('Skydio catalog query returned no data');
        }
        return page;
      };
      let mediaPages;
      if (selectedFlightIds.length > 0) {
        mediaPages = await loadFlightPreviews(
          selectedFlightIds,
          loadMediaPage,
          setCompletedFlights,
          controller.signal
        );
      } else {
        mediaPages = [await loadMediaPage()];
      }
      if (controller.signal.aborted) {
        return;
      }
      const resultFiles = [
        ...new Map(mediaPages.flatMap(page => page.files).map(file => [file.uuid, file])).values(),
      ];
      setMedia(resultFiles);
      const hasExactTotal =
        ingestedFilter === 'all' &&
        mediaPages.every(page => page.totalPages === 1 || page.totalCount > page.files.length);
      setTotalMediaCount(
        hasExactTotal ? mediaPages.reduce((total, page) => total + page.totalCount, 0) : undefined
      );
      setLoadedFilters(currentFilters);
      setSelectedRowKeys([]);
      setSelectAllMatching(false);
      setImportFlightIds([...selectedFlightIds]);
      setExcludedUuids([]);
      if (resultFiles.length === 0) {
        message.warning(
          t('inspection.skydio.noMediaMatched', 'No Skydio media matched those filters')
        );
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      message.error(
        t('inspection.skydio.mediaLoadError', 'Unable to load Skydio media: {{message}}', {
          message: error.message,
        })
      );
    } finally {
      if (!controller.signal.aborted) {
        setIsLoadingFlightCatalog(false);
      }
    }
  };

  const discoverFlights = async () => {
    if (isDiscoveringFlights || controlsDisabled) {
      return;
    }
    const controller = new AbortController();
    discoveryAbort.current = controller;
    setIsDiscoveringFlights(true);
    try {
      const flightFilters = {
        perPage: 500,
        takeoffBefore: capturedBefore || undefined,
        takeoffSince: capturedSince || undefined,
      };
      const loadFlightPage = async (pageNumber: number) => {
        const response = await queryFlights({
          variables: {
            input: { filters: { ...flightFilters, pageNumber } },
          },
        });
        if (response.error) {
          throw response.error;
        }
        const page = response.data?.skydioFlights;
        if (!page) {
          throw new Error(`Skydio flight query returned no data for page ${pageNumber}`);
        }
        return page;
      };
      const firstFlightPage = await loadFlightPage(1);
      const flightPages = [firstFlightPage.flights];
      for (let pageNumber = 2; pageNumber <= firstFlightPage.totalPages; pageNumber += 1) {
        if (controller.signal.aborted) {
          return;
        }
        const page = await loadFlightPage(pageNumber);
        flightPages.push(page.flights);
      }
      if (controller.signal.aborted) {
        return;
      }
      setFlights(
        [...new Map(flightPages.flat().map(flight => [flight.flightId, flight])).values()].sort(
          (left, right) => (right.takeoff ?? '').localeCompare(left.takeoff ?? '')
        )
      );
    } catch (_error) {
      if (controller.signal.aborted) {
        return;
      }
      message.warning(
        t(
          'inspection.skydio.flightDiscoveryUnavailable',
          'Flight discovery unavailable. Browse by date and image type, or enter flight IDs.'
        )
      );
    } finally {
      if (!controller.signal.aborted) {
        setIsDiscoveringFlights(false);
      }
    }
  };

  const startImport = async () => {
    if (
      controlsDisabled ||
      !previewIsCurrent ||
      (!selectAllMatching && selectedMedia.length === 0) ||
      !targetInspectionId
    ) {
      return;
    }
    setIsImportInProgress(true);
    try {
      const response = await runStartImport({
        variables: {
          input: {
            selection: selectAllMatching
              ? {
                  mode: AtlasGqlSkydioImportSelectionMode.AllMatching,
                  filters: loadedFilters,
                  excludedUuids,
                }
              : {
                  mode: AtlasGqlSkydioImportSelectionMode.Explicit,
                  media: selectedMedia.map(item => ({
                    uuid: item.uuid,
                    kind: item.kind,
                    filename: item.filename,
                  })),
                },
            inspectionId: targetInspectionId,
            releaseOnSuccess,
            thermalIronbowEnabled,
          },
        },
      });
      if (response.error) {
        throw response.error;
      }
      const result = response.data?.startSkydioImport;
      if (!result) {
        throw new Error('Start Skydio import returned no data');
      }
      setRecordId(result.recordId);
      saveSkydioImportRecord({
        bundleId: result.bundleId,
        inspectionId: targetInspectionId,
        recordId: result.recordId,
        selectedCount: selectAllMatching ? 0 : selectedMedia.length,
        startedAt: new Date().toISOString(),
      });
      message.success(t('inspection.skydio.importStarted', 'Skydio import started'));
    } catch (error) {
      setIsImportInProgress(false);
      message.error(
        t('inspection.skydio.importStartError', 'Unable to start Skydio import: {{message}}', {
          message: error.message,
        })
      );
    }
  };

  if (credentialStatus.error) {
    return (
      <SkydioAlert
        type="error"
        showIcon
        message={t(
          'inspection.skydio.credentialStatusLoadError',
          'Unable to load Skydio status: {{message}}',
          { message: credentialStatus.error.message }
        )}
        description={
          <Button onClick={() => credentialStatus.refetch()}>
            {t('inspection.skydio.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  if (configured === false) {
    return (
      <SkydioAlert
        type="warning"
        showIcon
        message={t('inspection.skydio.notConfigured', 'Skydio is not configured')}
        description={
          <span>
            {t('inspection.skydio.addApiKeyIn', 'Add a Skydio API key in')}{' '}
            <Link to="/settings/integrations">
              {t('inspection.skydio.settingsIntegrations', 'Settings > Integrations')}
            </Link>{' '}
            {t('inspection.skydio.beforeImporting', 'before importing Skydio media.')}
          </span>
        }
      />
    );
  }

  if (configured === undefined) {
    return (
      <SkydioAlert
        type="info"
        showIcon
        message={t('inspection.skydio.checkingIntegration', 'Checking Skydio integration')}
      />
    );
  }

  const capturedSinceDate = toUtcPickerValue(capturedSince);
  const capturedBeforeDate = toUtcPickerValue(capturedBefore);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <UTCDatePicker
          disabled={controlsDisabled}
          value={capturedSinceDate}
          onChange={(date: Moment | null) =>
            setCapturedSince(date ? date.startOf('day').toISOString() : '')
          }
          placeholder={t('inspection.skydio.capturedSince', 'Captured since')}
          style={{ width: 180 }}
        />
        <UTCDatePicker
          disabled={controlsDisabled}
          value={capturedBeforeDate}
          onChange={(date: Moment | null) =>
            setCapturedBefore(date ? date.endOf('day').toISOString() : '')
          }
          placeholder={t('inspection.skydio.capturedBefore', 'Captured before')}
          style={{ width: 180 }}
        />
        <Select
          disabled={controlsDisabled}
          mode="multiple"
          value={kinds}
          options={getMediaKindOptions(t)}
          aria-label={t('inspection.skydio.kindColumn', 'Kind')}
          onChange={setKinds}
          style={{ minWidth: 250 }}
        />
        <Select
          disabled={controlsDisabled}
          value={ingestedFilter}
          aria-label={t('inspection.skydio.statusColumn', 'Status')}
          onChange={setIngestedFilter}
          options={[
            { label: t('inspection.skydio.allStatuses', 'All statuses'), value: 'all' },
            { label: t('inspection.skydio.newStatus', 'New'), value: 'new' },
            { label: t('inspection.skydio.ingestedStatus', 'Ingested'), value: 'ingested' },
          ]}
          style={{ width: 140 }}
        />
      </Space>

      <Space wrap>
        <div
          onPaste={event => {
            if (controlsDisabled) {
              return;
            }
            const ids = event.clipboardData
              .getData('text')
              .split(/[\s,;]+/)
              .map(normalizeFlightId)
              .filter(Boolean);
            if (!ids.length) {
              return;
            }
            event.preventDefault();
            setSelectedFlightIds(previous => [...new Set([...previous, ...ids])]);
            setFlightSearch('');
          }}
        >
          <Select
            aria-label={t('inspection.skydio.flightIds', 'Flight IDs')}
            disabled={controlsDisabled}
            mode="tags"
            value={selectedFlightIds}
            options={flightOptions}
            searchValue={flightSearch}
            onSearch={setFlightSearch}
            filterOption={(search, option) =>
              Boolean(
                option?.value.includes(normalizeFlightId(search)) ||
                  option?.label.toLowerCase().includes(search.toLowerCase())
              )
            }
            onChange={values => {
              setSelectedFlightIds([...new Set(values.map(normalizeFlightId).filter(Boolean))]);
              setFlightSearch('');
            }}
            placeholder={t(
              'inspection.skydio.selectFlightsPlaceholder',
              'Select or paste flight IDs'
            )}
            tokenSeparators={[',', ';', ' ', '\n', '\r']}
            maxTagCount={3}
            style={{ width: 360, maxWidth: 'calc(100vw - 100px)' }}
          />
        </div>
        <Button disabled={controlsDisabled} loading={isLoadingFlightCatalog} onClick={loadMedia}>
          {t('inspection.skydio.browse', 'Load media previews')}
        </Button>
        <Button
          disabled={controlsDisabled || isDiscoveringFlights}
          loading={isDiscoveringFlights}
          onClick={discoverFlights}
        >
          {t('inspection.skydio.discoverFlights', 'Find flights (optional)')}
        </Button>
      </Space>

      {isLoadingFlightCatalog && selectedFlightIds.length > 0 ? (
        <div role="status">
          {t('inspection.skydio.loadingFlights', '{{completed}} of {{total}} flights loaded', {
            completed: completedFlights,
            total: selectedFlightIds.length,
          })}
        </div>
      ) : null}

      {loadedFilters ? (
        <SkydioAlert
          type="info"
          showIcon
          message={
            totalMediaCount === undefined
              ? t(
                  'inspection.skydio.previewUnknown',
                  '{{visibleCount}} preview records; full media count determined during import',
                  { visibleCount: filteredMedia.length }
                )
              : t(
                  'inspection.skydio.partialCatalog',
                  '{{visibleCount}} preview records of {{totalCount}} matching media',
                  { visibleCount: filteredMedia.length, totalCount: totalMediaCount }
                )
          }
          description={
            importFlightIds.length
              ? t(
                  'inspection.skydio.flightPreviewSummary',
                  '{{count}} flights. Up to {{limit}} preview records per flight.',
                  { count: importFlightIds.length, limit: PREVIEW_PAGE_SIZE }
                )
              : t('inspection.skydio.catalogPreviewSummary', 'Up to {{limit}} preview records.', {
                  limit: PREVIEW_PAGE_SIZE,
                })
          }
        />
      ) : null}
      {loadedFilters && !previewIsCurrent ? (
        <div role="status">
          {t(
            'inspection.skydio.refreshPreviews',
            'Filters changed. Refresh media previews before importing.'
          )}
        </div>
      ) : null}

      <ImportScope
        value={selectAllMatching ? 'all' : 'selected'}
        disabled={controlsDisabled || !previewIsCurrent}
        onChange={event => {
          const all = event.target.value === 'all';
          setSelectAllMatching(all);
          setExcludedUuids([]);
          setSelectedRowKeys(all ? filteredMedia.map(item => item.uuid) : []);
        }}
      >
        <Space direction="vertical">
          <Radio value="all">{allLabel}</Radio>
          <Radio value="selected">
            {t('inspection.skydio.selectedPreviewScope', 'Selected preview rows only')}
          </Radio>
        </Space>
      </ImportScope>

      <SkydioMediaTable
        disabled={controlsDisabled || !previewIsCurrent}
        loading={isLoadingFlightCatalog}
        media={filteredMedia}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={keys => {
          setSelectedRowKeys(keys);
          if (selectAllMatching) {
            setExcludedUuids(
              filteredMedia.filter(item => !keys.includes(item.uuid)).map(item => item.uuid)
            );
          }
        }}
      />

      <Space direction="vertical">
        <Checkbox
          checked={releaseOnSuccess}
          disabled={controlsDisabled}
          onChange={event => setReleaseOnSuccess(event.target.checked)}
        >
          {t('inspection.skydio.releaseAfterImport', 'Release target Inspections after import')}
        </Checkbox>
        <Checkbox
          checked={thermalIronbowEnabled}
          disabled={controlsDisabled}
          onChange={event => setThermalIronbowEnabled(event.target.checked)}
        >
          {t(
            'inspection.skydio.generateIronbow',
            'Generate ironbow thermal images when radiometric media is selected'
          )}
        </Checkbox>
      </Space>

      <SkydioAlert
        type="info"
        showIcon
        message={t(
          'inspection.skydio.selectionSummary',
          '{{selectedCount}} selected, {{ingestedCount}} already ingested',
          {
            selectedCount: selectAllMatching
              ? importCount ?? t('inspection.skydio.allMatching', 'All matching')
              : selectedMedia.length,
            ingestedCount: selectedMedia.filter(item => item.ingested).length,
          }
        )}
        description={t(
          'inspection.skydio.importDescription',
          'Selected Skydio media will be imported into this Inspection'
        )}
      />

      <SkydioImportStatusPanel
        inspectionIds={inspectionIds}
        progress={progress}
        recordId={recordId}
      />

      {importMonitorError ? (
        <SkydioAlert
          type="error"
          showIcon
          message={t(
            'inspection.skydio.importMonitorError',
            'Unable to monitor Skydio import: {{message}}',
            { message: importMonitorError.message }
          )}
          description={
            <Button
              onClick={() => {
                setIsImportInProgress(true);
                retryImportMonitor();
              }}
            >
              {t('inspection.skydio.retryMonitoring', 'Retry monitoring')}
            </Button>
          }
        />
      ) : null}

      <Space>
        <Button
          type="primary"
          loading={startImportMutation.loading}
          disabled={
            (!selectAllMatching && selectedMedia.length === 0) ||
            !targetInspectionId ||
            controlsDisabled ||
            !previewIsCurrent ||
            importCount === 0
          }
          onClick={startImport}
        >
          {submitLabel}
        </Button>
      </Space>
    </Space>
  );
}
